import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { Message } from './messages/Message';
import { chatApi } from '../../../shared/api/chatApi';
import type { Database } from '../../../shared/lib/database.types';
import { ErrorMessage } from '../../../shared/components/error/ErrorMessage';
import { LoadingMessage } from './messages/LoadingMessage';
import { Header } from '../../../shared/components/layout/Header';
import { LandingHeader } from '../../../shared/components/layout/LandingHeader';
import { Sidebar } from '../../../shared/components/layout/Sidebar';
import { InfoSidebar } from '../../../shared/components/layout/InfoSidebar';
import { useBibleStore } from '../../../shared/store/bibleStore';

type MessageData = Database['public']['Tables']['messages']['Row'];

export default function SharedChat() {
    const { publicId } = useParams();
    const navigate = useNavigate();
    const { user, isInitialized } = useAuth();
    const [messages, setMessages] = useState<MessageData[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [title, setTitle] = useState<string>('');
    const [createdAt, setCreatedAt] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const { selectedVerse, selectedVerseText, isInfoSidebarOpen, setInfoSidebarOpen } = useBibleStore();

    useEffect(() => {
        async function loadSharedChat() {
            if (!publicId) {
                setError('No public ID provided');
                setIsLoading(false);
                return;
            }

            try {
                const { chat, messages } = await chatApi.getSharedChat(publicId);
                if (!chat) {
                    throw new Error('Chat not found');
                }
                setTitle(chat.title);
                setCreatedAt(chat.created_at);
                setMessages(messages || []);
            } catch (error) {
                console.error('Error loading shared chat:', error);
                setError('This chat does not exist or is no longer public.');
            } finally {
                setIsLoading(false);
            }
        }

        loadSharedChat();
    }, [publicId]);

    useEffect(() => {
        if (title) {
            document.title = `${title} - Theos AI Bible Chat`;
        } else {
            document.title = 'Theos AI Bible Chat';
        }

        return () => {
            document.title = 'Theos AI Bible Chat';
        };
    }, [title]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (isLoading) {
        return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <LoadingMessage />
        </div>;
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                <div className="max-w-md w-full">
                    <ErrorMessage
                        message="Chat Not Found"
                        action={() => navigate('/')}
                        actionLabel="Return Home"
                        variant="card"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex bg-[#ffffff]">
            {user && <Sidebar />}
            <div className={`flex-1 transition-all duration-300 ${isInfoSidebarOpen ? 'md:mr-[320px]' : 'mr-0'}`}>
                {user ? <Header /> : <LandingHeader color="#361E06" />}
                <div className="bg-[#F9F7F4] z-10 fixed w-full font-semibold text-center py-2 text-[#361E06] mt-[60px]">
                    You are viewing a shared chat
                </div>
                <main className="pt-32 pb-24">
                    <div className="max-w-2xl mx-auto px-4">
                        {title && (
                            <div className="text-center py-12 mb-8">
                                <h1 className="text-4xl font-semibold text-[#361E06] mb-4">{title}</h1>
                                {createdAt && (
                                    <p className="text-[#361E06] font-medium opacity-75">
                                        Chat created on {formatDate(createdAt)}
                                    </p>
                                )}
                            </div>
                        )}
                        <div className="space-y-[50px]">
                            {messages && messages.length > 0 ? (
                                messages.map((message) => (
                                    <Message
                                        key={message.id}
                                        content={message.content}
                                        role={message.role}
                                        isSharedView={true}
                                        onTellMeMore={() => {}}
                                        onFollowUpQuestion={() => {}}
                                        hideFollowUpQuestions={true}
                                    />
                                ))
                            ) : (
                                <div className="text-center text-gray-500">
                                    No messages to display
                                </div>
                            )}
                        </div>
                    </div>
                </main>

                {!user && (
                    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-[100]">
                        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="text-center sm:text-left">
                                <h3 className="text-lg font-semibold text-[#361E06]">Get more from Theos</h3>
                                <p className="text-gray-600">Create an account to save verses, start conversations, and more</p>
                            </div>
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full sm:w-auto px-8 py-3 bg-[#361E06] text-white rounded-lg hover:bg-[#4A2B0B] font-medium"
                            >
                                Create Account
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <InfoSidebar
                isOpen={isInfoSidebarOpen}
                onClose={() => setInfoSidebarOpen(false)}
                verse={selectedVerse || undefined}
                verseText={selectedVerseText || undefined}
            />
        </div>
    );
}